import { useState, useEffect, useCallback } from 'react';
import {
  getCasesForPlan,
  saveCaseToHistory,
  deleteCaseFromHistory,
  deleteMultipleCases,
  clearHistoryForPlan,
  getCaseByKey,
  markCaseAsTriggered,
  generateCaseKey,
} from '../services/caseHistoryStorage';

/**
 * Custom hook for managing case history state
 * Syncs with localStorage and provides methods for CRUD operations
 *
 * @param {string} planName - The current plan name to load history for
 * @param {Object} [orgContext={}] - Organization context for storage key
 * @param {string} [orgContext.orgGroupId] - Organization group ID
 * @param {string} [orgContext.organizationId] - Organization ID
 * @returns {object} Case history state and methods
 */
const useCaseHistory = (planName, orgContext) => {
  // State for current plan's cases
  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  // Stable reference for orgContext to use in dependencies
  const orgGroupId = orgContext.orgGroupId;
  const organizationId = orgContext.organizationId;

  /**
   * Load cases from localStorage when plan or org context changes
   */
  useEffect(() => {
    if (planName) {
      setIsLoading(true);
      const storedCases = getCasesForPlan(planName, { orgGroupId, organizationId });
      setCases(storedCases);
      setSelectedIds([]); // Reset selection when plan changes
      setIsLoading(false);
    } else {
      setCases([]);
      setSelectedIds([]);
    }
  }, [planName, orgGroupId, organizationId]);

  /**
   * Refresh cases from localStorage
   */
  const refreshCases = useCallback(() => {
    if (planName) {
      const storedCases = getCasesForPlan(planName, { orgGroupId, organizationId });
      setCases(storedCases);
    }
  }, [planName, orgGroupId, organizationId]);

  /**
   * Save a case to history
   * @param {object} formData - Form data to save
   * @param {object} customFieldValues - Custom field values
   * @param {boolean} wasTriggered - Whether case was triggered
   * @param {string} caseId - The case ID returned from API (optional)
   * @returns {object} Saved case record
   */
  const saveCase = useCallback(
    (formData, customFieldValues = {}, wasTriggered = false, caseId = null) => {
      if (!planName) {
        throw new Error('No plan selected');
      }

      const savedCase = saveCaseToHistory(planName, formData, customFieldValues, wasTriggered, caseId, { orgGroupId, organizationId });

      // Refresh local state
      refreshCases();

      return savedCase;
    },
    [planName, orgGroupId, organizationId, refreshCases]
  );

  /**
   * Get a case by its key
   * @param {string} caseKey - The case key
   * @returns {object|null} Case data or null
   */
  const getCase = useCallback(
    (caseKey) => {
      if (!planName) return null;
      return getCaseByKey(planName, caseKey, { orgGroupId, organizationId });
    },
    [planName, orgGroupId, organizationId]
  );

  /**
   * Delete a single case
   * @param {string} caseKey - The case key to delete
   * @returns {boolean} Success status
   */
  const deleteCase = useCallback(
    (caseKey) => {
      if (!planName) return false;

      const success = deleteCaseFromHistory(planName, caseKey, { orgGroupId, organizationId });

      if (success) {
        // Update local state
        setCases((prev) => prev.filter((c) => c.key !== caseKey));
        // Remove from selection if selected
        setSelectedIds((prev) => prev.filter((id) => id !== caseKey));
      }

      return success;
    },
    [planName, orgGroupId, organizationId]
  );

  /**
   * Delete multiple cases
   * @param {Array<string>} caseKeys - Array of case keys to delete
   * @returns {number} Number of cases deleted
   */
  const deleteCases = useCallback(
    (caseKeys) => {
      if (!planName || !caseKeys || caseKeys.length === 0) return 0;

      const deletedCount = deleteMultipleCases(planName, caseKeys, { orgGroupId, organizationId });

      if (deletedCount > 0) {
        // Update local state
        const keySet = new Set(caseKeys);
        setCases((prev) => prev.filter((c) => !keySet.has(c.key)));
        setSelectedIds([]);
      }

      return deletedCount;
    },
    [planName, orgGroupId, organizationId]
  );

  /**
   * Clear all history for current plan
   * @returns {boolean} Success status
   */
  const clearHistory = useCallback(() => {
    if (!planName) return false;

    const success = clearHistoryForPlan(planName, { orgGroupId, organizationId });

    if (success) {
      setCases([]);
      setSelectedIds([]);
    }

    return success;
  }, [planName, orgGroupId, organizationId]);

  /**
   * Mark a case as triggered (updates triggeredAt timestamp)
   * @param {string} caseKey - The case key
   * @param {string} caseId - The case ID returned from API (optional)
   * @returns {object|null} Updated case or null
   */
  const triggerCase = useCallback(
    (caseKey, caseId = null) => {
      if (!planName) return null;

      const updatedCase = markCaseAsTriggered(planName, caseKey, caseId, { orgGroupId, organizationId });

      if (updatedCase) {
        refreshCases();
      }

      return updatedCase;
    },
    [planName, orgGroupId, organizationId, refreshCases]
  );

  /**
   * Re-trigger multiple cases
   * Returns the case data for each to be processed by caller
   * @param {Array<string>} caseKeys - Array of case keys
   * @returns {Array<object>} Array of case data to re-trigger
   */
  const getCasesForRetrigger = useCallback(
    (caseKeys) => {
      if (!planName || !caseKeys || caseKeys.length === 0) return [];

      return caseKeys
        .map((key) => getCaseByKey(planName, key, { orgGroupId, organizationId }))
        .filter((c) => c !== null);
    },
    [planName, orgGroupId, organizationId]
  );

  // Selection handlers
  const selectCase = useCallback((caseKey) => {
    setSelectedIds((prev) =>
      prev.includes(caseKey)
        ? prev.filter((id) => id !== caseKey)
        : [...prev, caseKey]
    );
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(cases.map((c) => c.key));
  }, [cases]);

  const deselectAll = useCallback(() => {
    setSelectedIds([]);
  }, []);

  /**
   * Check if a case exists for given patient name
   * @param {string} firstName - First name
   * @param {string} lastName - Last name
   * @returns {boolean} True if exists
   */
  const hasCase = useCallback(
    (firstName, lastName) => {
      const key = generateCaseKey(firstName, lastName);
      return cases.some((c) => c.key === key);
    },
    [cases]
  );

  // Computed values
  const allSelected = cases.length > 0 && selectedIds.length === cases.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < cases.length;
  const selectedCases = cases.filter((c) => selectedIds.includes(c.key));

  return {
    // State
    cases,
    isLoading,
    selectedIds,
    selectedCases,

    // Computed
    allSelected,
    someSelected,
    caseCount: cases.length,

    // CRUD operations
    saveCase,
    getCase,
    deleteCase,
    deleteCases,
    clearHistory,
    refreshCases,

    // Trigger operations
    triggerCase,
    getCasesForRetrigger,

    // Selection operations
    selectCase,
    selectAll,
    deselectAll,

    // Utilities
    hasCase,
    generateCaseKey,
  };
};

export default useCaseHistory;
